// Shared workout-duration estimator — used for the planning estimate and the
// intra-workout "time to finish". Pure functions, no network.
//
// Model (seconds):
//  - strength set work time: exercise.avg_set_seconds if set, else a category default
//  - rest: per-set rest_time (from target_set_plan[i].rest_time) or exercise.rest_time,
//          applied after each set (acts as set-to-set + transition time)
//  - superset group (same superset_group): members done back-to-back, ONE group rest
//          per round; rounds = max set count among members
//  - cardio: its planned `duration` (minutes) as work time
//
// All estimates are deliberately simple and slightly conservative.

const DEFAULT_SET_SECONDS = {
  compound: 45,
  strength: 40,
  isolation: 35,
  bodyweight: 35,
  core: 35,
  mobility: 30,
  stretching: 30,
  default: 40,
};
const DEFAULT_REST_SECONDS = 60;
const INTRA_GROUP_TRANSITION = 10; // seconds between paired superset moves

const isCardio = (ex) => (ex?.exercise_type || '').toLowerCase() === 'cardio';

const setCount = (ex) => {
  if (Array.isArray(ex?.target_set_plan) && ex.target_set_plan.length) return ex.target_set_plan.length;
  return Number(ex?.target_sets ?? ex?.sets ?? 3) || 3;
};

const workSecondsPerSet = (ex) => {
  if (ex?.avg_set_seconds != null && !isNaN(ex.avg_set_seconds)) return Number(ex.avg_set_seconds);
  if (ex?.learned_avg_set_seconds != null && !isNaN(ex.learned_avg_set_seconds)) return Number(ex.learned_avg_set_seconds);
  const key = (ex?.category || ex?.exercise_type || 'default').toLowerCase();
  return DEFAULT_SET_SECONDS[key] ?? DEFAULT_SET_SECONDS.default;
};

// Effective per-set work seconds + where it came from (for the library chip).
// Priority: manual override → auto-learned → category default.
export const effectiveSetSeconds = (ex) => {
  if (ex?.avg_set_seconds != null && !isNaN(ex.avg_set_seconds)) {
    return { seconds: Number(ex.avg_set_seconds), source: 'manual' };
  }
  if (ex?.learned_avg_set_seconds != null && !isNaN(ex.learned_avg_set_seconds)) {
    return { seconds: Number(ex.learned_avg_set_seconds), source: 'learned' };
  }
  const key = (ex?.category || ex?.exercise_type || 'default').toLowerCase();
  return { seconds: DEFAULT_SET_SECONDS[key] ?? DEFAULT_SET_SECONDS.default, source: 'default' };
};

const restSecondsForSet = (ex, i) => {
  const plan = ex?.target_set_plan;
  if (Array.isArray(plan) && plan[i] && plan[i].rest_time != null) return Number(plan[i].rest_time);
  if (ex?.rest_time != null) return Number(ex.rest_time);
  return DEFAULT_REST_SECONDS;
};

// Seconds for a single (non-superset) exercise, optionally only counting sets
// from `fromSet` onward (used for the intra-workout remaining estimate).
const singleExerciseSeconds = (ex, fromSet = 0) => {
  if (isCardio(ex)) {
    const mins = Number(ex?.duration ?? 0);
    return Math.max(0, mins) * 60;
  }
  const n = setCount(ex);
  let total = 0;
  for (let i = Math.max(0, fromSet); i < n; i++) {
    total += workSecondsPerSet(ex) + restSecondsForSet(ex, i);
  }
  return total;
};

// Groups consecutive-or-not exercises that share a superset_group; returns an
// ordered list of "units" where each unit is either one exercise or a group.
const buildUnits = (exercises) => {
  const units = [];
  const seenGroups = new Set();
  exercises.forEach((ex, idx) => {
    const grp = ex?.is_superset ? ex?.superset_group : null;
    if (grp) {
      if (seenGroups.has(grp)) return;
      seenGroups.add(grp);
      const members = exercises
        .map((e, i) => ({ e, i }))
        .filter(({ e }) => e?.is_superset && e?.superset_group === grp);
      units.push({ type: 'group', group: grp, members, firstIndex: idx });
    } else {
      units.push({ type: 'single', ex, index: idx });
    }
  });
  return units;
};

const groupSeconds = (members) => {
  const rounds = Math.max(...members.map(({ e }) => setCount(e)), 1);
  const perRoundWork = members.reduce((s, { e }) => s + workSecondsPerSet(e), 0)
    + INTRA_GROUP_TRANSITION * Math.max(0, members.length - 1);
  const groupRest = Math.max(...members.map(({ e }) => restSecondsForSet(e, 0)), DEFAULT_REST_SECONDS);
  return rounds * (perRoundWork + groupRest);
};

// Total planned seconds for a full exercise list.
export const estimateWorkoutSeconds = (exercises = []) => {
  if (!Array.isArray(exercises) || exercises.length === 0) return 0;
  const units = buildUnits(exercises);
  return units.reduce((total, u) => {
    if (u.type === 'group') return total + groupSeconds(u.members);
    return total + singleExerciseSeconds(u.ex);
  }, 0);
};

// Remaining planned seconds from the client's current position (intra-workout).
// `currentIndex` = current exercise index; `completedSetsInCurrent` = sets already
// logged for the current exercise.
export const estimateRemainingSeconds = (exercises = [], currentIndex = 0, completedSetsInCurrent = 0) => {
  if (!Array.isArray(exercises) || exercises.length === 0) return 0;
  let total = 0;
  for (let idx = 0; idx < exercises.length; idx++) {
    if (idx < currentIndex) continue;
    const ex = exercises[idx];
    // Superset members are estimated once (at the group's first member) to avoid
    // double counting; skip later members.
    if (ex?.is_superset && ex?.superset_group) {
      const firstMemberIdx = exercises.findIndex((e) => e?.is_superset && e?.superset_group === ex.superset_group);
      if (idx !== firstMemberIdx && firstMemberIdx >= currentIndex) continue;
      if (idx === firstMemberIdx) {
        const members = exercises.map((e, i) => ({ e, i })).filter(({ e }) => e?.is_superset && e?.superset_group === ex.superset_group);
        total += groupSeconds(members);
        continue;
      }
    }
    const fromSet = idx === currentIndex ? completedSetsInCurrent : 0;
    total += singleExerciseSeconds(ex, fromSet);
  }
  return total;
};

// Adaptive remaining time (Q3-b): scale the planned remaining by the client's
// actual pace so far. paceFactor = actualElapsed / plannedForCompleted, clamped.
export const adaptiveRemainingSeconds = (plannedRemaining, plannedCompleted, actualElapsedSeconds) => {
  if (!plannedCompleted || plannedCompleted <= 0 || !actualElapsedSeconds) return plannedRemaining;
  let factor = actualElapsedSeconds / plannedCompleted;
  factor = Math.min(2, Math.max(0.5, factor));
  return plannedRemaining * factor;
};

export const formatDuration = (seconds) => {
  const total = Math.max(0, Math.round(seconds));
  const m = Math.round(total / 60);
  if (m < 1) return '<1 min';
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h}h ${rem}m` : `${h}h`;
};
